import React, { useState, useEffect } from "react";
import { Popover, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { useHat } from "../../context/HatContext";
import { CheckIcon, LoaderCircle, Star, Search } from "lucide-react";
import { useUser } from "../../context/UserContext";
import Tooltip from "../Tooltip/Tooltip";

// panelAlign "start" opens the list from the button's left edge, for a
// button near the left of a dialog, where the centred default would be cut off.
const HatInput = ({ title = "Virtual Assistant", panelAlign = "center" }: { title?: string; panelAlign?: "center" | "start" }) => { 
  const [isHovered, setIsHovered] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAllHats, setShowAllHats] = useState(false);
  const [isUpdatingFavorite, setIsUpdatingFavorite] = useState<string | null>(null);

  const {
    hatsList = [],
    fetchHatsList,
    fetchHatsListWithFilter,
    currentFilter,
    filterLocked,
    selectedHat,
    setSelectedHat,
    loading,
    favoriteHats,
    toggleFavorite,
  } = useHat();
  
  const { userId } = useUser();

  // Function to load hat list
  const loadHatList = async () => {
    try {
      // fetchHatsList() does nothing while the VA settings filter is locked, which
      // left a VA created in this session out of the list; reload under that filter.
      if (filterLocked && currentFilter !== 'all') {
        await fetchHatsListWithFilter(currentFilter);
      } else {
        await fetchHatsList();
      }
    } catch (error) {
      console.error("Error loading hats:", error);
    }
  };

  // Initial load of hats list
  useEffect(() => {
    loadHatList();
  }, [fetchHatsList, userId]);

  // Add refresh when favorites change
  useEffect(() => {
    if (!showAllHats) {
      loadHatList();
    }
  }, [showAllHats]);

  // Helper function to check if a hat is favorited (handles both DynamoDB formats)
  const isHatFavorited = (hatId: string) => {
    const hat = hatsList.find(h => h.id === hatId);
    if (hat?.favorites) {
      // Handle both DynamoDB BOOL structure and simple boolean
      const favoriteValue = hat.favorites[userId];
      if (typeof favoriteValue === 'object' && favoriteValue?.BOOL !== undefined) {
        return favoriteValue.BOOL === true;
      }
      return favoriteValue === true;
    }
    return false;
  };

  const getIconState = () => {
    if (selectedHat) {
      return true; 
    }
    return isHovered; 
  };

  const handleHatClick = (event: React.MouseEvent, hat: any) => {
    event.preventDefault();
    if (selectedHat?.id === hat.id) {
      setSelectedHat(null);
    } else {
      setSelectedHat(hat);
    }
  };

  const handleToggleFavorite = async (e: React.MouseEvent, hatId: string) => {
    e.stopPropagation();

    if (isUpdatingFavorite === hatId) {
      return;
    }

    try {
      setIsUpdatingFavorite(hatId);
      
      await toggleFavorite(hatId);
      
      // Refresh the list
      await loadHatList();
      
    } catch (error) {
      console.error('Error toggling favorite:', error);
    } finally {
      setIsUpdatingFavorite(null);
    }
  };

  const handleViewToggle = async () => {
    const newShowAllHats = !showAllHats;
    setShowAllHats(newShowAllHats);
    await loadHatList(); 
  };

  const filteredHats = React.useMemo(() => {
    let filtered = hatsList;
    
    // If there's a search query, filter by search regardless of view mode
    if (searchQuery) {
      filtered = hatsList.filter(hat =>
       hat.hat_name && hat.hat_name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      return filtered;
    }

    // If not showing all hats (showing favorites only)
    if (!showAllHats) {
      filtered = hatsList.filter(hat => isHatFavorited(hat.id || ''));
    }

    return filtered;
  }, [hatsList, showAllHats, searchQuery, userId]);

  // Render star button with loading state
  const renderStarButton = (hat: any) => {
    const isLoading = isUpdatingFavorite === hat.id;
    const isFavorited = isHatFavorited(hat.id || '');
    
    return (
      <button
        onClick={(e) => handleToggleFavorite(e, hat.id || '')}
        disabled={isLoading}
        className={`
          relative
          ${isFavorited
            ? "text-green-500"
            : "text-gray-400 hover:text-gray-600"}
          ${isLoading ? 'cursor-wait' : 'cursor-pointer'}
        `}
      >
        {isLoading ? (
          <LoaderCircle className="animate-spin" size={14} />
        ) : (
          <Star
            size={14}
            className={isFavorited ? "fill-current" : ""}
          />
        )}
      </button>
    );
  };

  return (
    <Popover className="relative">
      <Tooltip label={title} side="top">
        <Popover.Button
          className="bg-surface-2 text-muted hover:bg-surface-3 hover:text-primary focus:outline-none transition-colors duration-200 p-2 rounded-full relative"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          // Reload on every open, so a VA created since this chat box mounted shows.
          onClick={loadHatList}
        >
          <span className="flex items-center">
            <img
              src={getIconState() ? '/va-icon.svg' : '/va-icon-bw.svg'}
              alt="Virtual Assistant Icon"
              className="w-[18px] h-[18px]"
            />
          </span>
        </Popover.Button>
      </Tooltip>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-150"
        enterFrom="opacity-0 translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-1"
      >
        <Popover.Panel className={`absolute z-10 bg-surface-overlay text-primary ring-1 ring-border-default shadow-lg rounded-lg p-2 mt-3 w-[320px] max-h-[400px] overflow-hidden ${panelAlign === "start" ? "left-0" : "-right-21 -left-20 sm:-right-20"}`}>
          {loading ? (
            <div className="flex justify-center items-center p-4">
              <LoaderCircle className="animate-spin text-muted" size={17} />
            </div>
          ) : (
            <div className="space-y-2">
              {/* View toggle - NEW: Restored from previous version */}
              <button
  onClick={handleViewToggle}
  className={`w-full text-sm px-3 py-1.5 rounded-md flex items-center justify-between flex-shrink-0
    ${showAllHats
      ? "text-muted hover:bg-surface-2"
      : "text-primary bg-surface-2 hover:bg-surface-3"}`}
>
                <span>{showAllHats ? 'Showing all Virtual Assistants' : 'Showing favorites'}</span>
                <Star
                  size={14} 
                  className={!showAllHats ? 'fill-current' : ''} 
                />
              </button>

              {/* Search input */}
              <div className="relative flex-shrink-0">
                <input
                  type="search"
                  placeholder="Search VA's..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-3 py-1.5 pr-8 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
                <Search size={14} className="absolute right-2.5 top-2 text-gray-400" />
              </div>

              {/* Hat list */}
              <div className="max-h-[280px] overflow-y-auto pb-2">
                {filteredHats.length > 0 ? (
                  filteredHats.map((hat) => (
                    <button
                      key={hat.id}
                      onClick={(event) => handleHatClick(event, hat)}
                      className="flex items-center justify-between w-full p-2 rounded-lg hover:bg-surface-2 transition group"
                    >
                      <span
                        className={`text-sm font-medium whitespace-nowrap text-left flex-grow pr-2
                          ${selectedHat?.id === hat.id ? "text-primary font-semibold" : "text-primary"}`}
                      >
                        {hat.hat_name || 'Unnamed VA'}
                      </span>
                      <div className="flex items-center space-x-2">
                        {renderStarButton(hat)}
                        {selectedHat?.id === hat.id && (
                          <CheckIcon className="text-green-500 w-4 h-4" />
                        )}
                      </div>
                    </button>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">
                    {showAllHats 
                      ? searchQuery 
                        ? "No VA's match your search" 
                        : "No VA's available"
                      : searchQuery
                        ? "No VA's match your search"
                        : "No favorite VA's yet"}
                  </p>
                )}
              </div>
            </div>
          )}
        </Popover.Panel>
      </Transition>
    </Popover>
  );
};

export default HatInput;